import { useEffect, useMemo, useRef, useState } from 'react';
import type { StreamEvent } from '@hypevault/shared-types';

/**
 * Marathon (Full) — mission-control panel.
 *
 * Glassy dark card with drifting radial-gradient ambience, a massive
 * gradient-painted countdown hero, a four-stat strip (initial · bonus ·
 * supporters · events), a top-5 lifetime leaderboard, and a live activity
 * feed. On every contribution: the timer pulses, a top-right banner
 * slides in with "+time · name", the activity feed gets a new row sliding
 * in from the top with a brief accent flash, and the ambient glow briefly
 * intensifies.
 *
 * Same data pipeline as Marathon (Compact) and Marathon (Minimal) —
 * polls /api/marathon/public every 2s, refetches on every StreamEvent,
 * accepts `testReveal` from the dashboard tester via the shared
 * `marathon-reveal` socket event. The public endpoint was extended with
 * `topContributors[]`, `contributionCount`, and `uniqueContributorCount`
 * specifically to feed this widget's stats + leaderboard.
 *
 * Config (all optional):
 *   gradient: 'sunset'|'aurora'|'fire'|'ocean'|'candy'   (default 'sunset')
 *   bannerHoldSeconds: number                            (default 3.5)
 *   showLeaderboard: boolean                             (default true)
 *   showActivity: boolean                                (default true)
 *   showStats: boolean                                   (default true)
 *   maxLeaderboardEntries: number                        (default 5)
 *   maxActivityItems: number                             (default 6)
 */

interface TopContributor {
  contributorName: string | null;
  totalSeconds: number;
  contributionCount: number;
}

interface MarathonSnapshot {
  isActive: boolean;
  name: string | null;
  endsAt: string | null;
  pausedAt: string | null;
  startedAt: string | null;
  initialDurationSec: number | null;
  recent: Array<{
    contributorName: string | null;
    eventType: string;
    secondsAdded: number;
    createdAt: string;
  }>;
  topContributors: TopContributor[];
  contributionCount: number;
  uniqueContributorCount: number;
}

interface RevealItem {
  key: string;
  contributorName: string;
  eventType: string;
  secondsAdded: number;
}

interface TestReveal {
  id: string;
  eventType: string;
  contributorName: string;
  secondsAdded: number;
  createdAt: string;
}

const GRADIENTS: Record<string, string> = {
  sunset: 'linear-gradient(135deg, #f655a6 0%, #ff8a4c 50%, #ffcc33 100%)',
  aurora: 'linear-gradient(135deg, #22d3ee 0%, #a855f7 50%, #f655a6 100%)',
  fire: 'linear-gradient(135deg, #ef4444 0%, #f97316 50%, #facc15 100%)',
  ocean: 'linear-gradient(135deg, #14b8a6 0%, #3b82f6 50%, #8b5cf6 100%)',
  candy: 'linear-gradient(135deg, #f655a6 0%, #c084fc 50%, #60a5fa 100%)',
};

const GRADIENT_ACCENT: Record<string, string> = {
  sunset: '#ff8a4c',
  aurora: '#a855f7',
  fire: '#f97316',
  ocean: '#3b82f6',
  candy: '#c084fc',
};

const GRADIENT_ACCENT_RGB: Record<string, string> = {
  sunset: '255,138,76',
  aurora: '168,85,247',
  fire: '249,115,22',
  ocean: '59,130,246',
  candy: '192,132,252',
};

const EVENT_LABEL: Record<string, string> = {
  follow: 'FOLLOW',
  kick_follow: 'KICK FOLLOW',
  sub_t1: 'SUB',
  sub_t2: 'TIER 2 SUB',
  sub_t3: 'TIER 3 SUB',
  sub_prime: 'PRIME SUB',
  yt_member: 'MEMBER',
  kick_sub: 'KICK SUB',
  kick_gift_sub: 'GIFT KICK SUB',
  cheer: 'CHEER',
  kick_kicks: 'KICKS',
  raid: 'RAID',
  yt_superchat: 'SUPERCHAT',
  yt_supersticker: 'STICKER',
  tip: 'TIP',
  hypevault_card_redemption: 'CARD',
  hypevault_card_purchase: 'PURCHASE',
  manual_adjust: 'HOST',
};

const EVENT_GLYPH: Record<string, string> = {
  follow: '✚',
  kick_follow: '✚',
  sub_t1: '★',
  sub_t2: '★',
  sub_t3: '★',
  sub_prime: '★',
  yt_member: '★',
  kick_sub: '★',
  kick_gift_sub: '★',
  cheer: '◆',
  kick_kicks: '◆',
  raid: '⚡',
  yt_superchat: '$',
  yt_supersticker: '$',
  tip: '◉',
  hypevault_card_redemption: '✦',
  hypevault_card_purchase: '◈',
  manual_adjust: '⚙',
};

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) seconds = 0;
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function formatAdded(seconds: number): string {
  if (seconds < 60) return `+${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s === 0 ? `+${m}m` : `+${m}m ${s}s`;
}

function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) seconds = 0;
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

export function MarathonFullWidget({
  fields,
  token,
  latestEvent,
  testReveal,
}: {
  fields: Record<string, unknown>;
  token: string;
  latestEvent: StreamEvent | null;
  testReveal?: TestReveal | null;
}) {
  const gradientKey = String(fields.gradient ?? 'sunset');
  const gradient = GRADIENTS[gradientKey] ?? GRADIENTS.sunset!;
  const accent = GRADIENT_ACCENT[gradientKey] ?? GRADIENT_ACCENT.sunset!;
  const accentRgb = GRADIENT_ACCENT_RGB[gradientKey] ?? GRADIENT_ACCENT_RGB.sunset!;
  const bannerHoldMs = Math.max(1500, Number(fields.bannerHoldSeconds ?? 3.5) * 1000);
  const showLeaderboard = (fields.showLeaderboard as boolean | undefined) ?? true;
  const showActivity = (fields.showActivity as boolean | undefined) ?? true;
  const showStats = (fields.showStats as boolean | undefined) ?? true;
  const maxLeaderboardEntries = Number(fields.maxLeaderboardEntries ?? 5);
  const maxActivityItems = Number(fields.maxActivityItems ?? 6);

  const [snapshot, setSnapshot] = useState<MarathonSnapshot | null>(null);
  const [remaining, setRemaining] = useState(0);
  const [reveal, setReveal] = useState<RevealItem | null>(null);
  const [queue, setQueue] = useState<RevealItem[]>([]);
  // Flips on every reveal mount — drives a one-shot pulse CSS animation on
  // the timer hero via a re-keyed key prop.
  const [pulseKey, setPulseKey] = useState(0);

  const lastSeenCreatedAtRef = useRef<string | null>(null);
  const lastEventIdRef = useRef<string | null>(null);
  const lastTestRevealIdRef = useRef<string | null>(null);

  // ───── Snapshot fetch + diff (2s poll) ─────
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const load = () => {
      fetch(`/api/marathon/public?token=${encodeURIComponent(token)}`, { cache: 'no-store' })
        .then((r) => (r.ok ? (r.json() as Promise<MarathonSnapshot>) : null))
        .then((data) => {
          if (cancelled || !data) return;
          setSnapshot(data);
          if (!data.recent || data.recent.length === 0) return;
          const newest = data.recent[0]!.createdAt;
          if (lastSeenCreatedAtRef.current === null) {
            lastSeenCreatedAtRef.current = newest;
            return;
          }
          const lastSeen = lastSeenCreatedAtRef.current;
          const fresh = data.recent
            .filter((r) => r.createdAt > lastSeen && r.secondsAdded > 0)
            .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
          if (fresh.length === 0) return;
          const items: RevealItem[] = fresh.map((r) => ({
            key: r.createdAt,
            contributorName: r.contributorName ?? 'Someone',
            eventType: r.eventType,
            secondsAdded: r.secondsAdded,
          }));
          setQueue((q) => [...q, ...items]);
          lastSeenCreatedAtRef.current = newest;
        })
        .catch(() => undefined);
    };
    load();
    const interval = setInterval(load, 2000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [token]);

  // ───── Refetch on every StreamEvent ─────
  useEffect(() => {
    if (!latestEvent || !token) return;
    if (latestEvent.id === lastEventIdRef.current) return;
    lastEventIdRef.current = latestEvent.id;
    const t = setTimeout(() => {
      fetch(`/api/marathon/public?token=${encodeURIComponent(token)}`, { cache: 'no-store' })
        .then((r) => (r.ok ? (r.json() as Promise<MarathonSnapshot>) : null))
        .then((data) => {
          if (!data) return;
          setSnapshot(data);
          if (!data.recent || data.recent.length === 0) return;
          const lastSeen = lastSeenCreatedAtRef.current;
          if (lastSeen === null) {
            lastSeenCreatedAtRef.current = data.recent[0]!.createdAt;
            return;
          }
          const fresh = data.recent
            .filter((r) => r.createdAt > lastSeen && r.secondsAdded > 0)
            .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
          if (fresh.length === 0) return;
          const items: RevealItem[] = fresh.map((r) => ({
            key: r.createdAt,
            contributorName: r.contributorName ?? 'Someone',
            eventType: r.eventType,
            secondsAdded: r.secondsAdded,
          }));
          setQueue((q) => [...q, ...items]);
          lastSeenCreatedAtRef.current = data.recent[0]!.createdAt;
        })
        .catch(() => undefined);
    }, 300);
    return () => clearTimeout(t);
  }, [latestEvent, token]);

  // ───── Test reveals ─────
  useEffect(() => {
    if (!testReveal) return;
    if (testReveal.id === lastTestRevealIdRef.current) return;
    lastTestRevealIdRef.current = testReveal.id;
    setQueue((q) => [
      ...q,
      {
        key: testReveal.id,
        contributorName: testReveal.contributorName,
        eventType: testReveal.eventType,
        secondsAdded: testReveal.secondsAdded,
      },
    ]);
  }, [testReveal]);

  // ───── Local tick (smooth countdown between polls) ─────
  useEffect(() => {
    if (!snapshot) return;
    const tick = () => {
      if (!snapshot.endsAt) {
        setRemaining(0);
        return;
      }
      const endsAt = new Date(snapshot.endsAt).getTime();
      const ref = snapshot.pausedAt ? new Date(snapshot.pausedAt).getTime() : Date.now();
      setRemaining(Math.max(0, Math.floor((endsAt - ref) / 1000)));
    };
    tick();
    const interval = setInterval(tick, 500);
    return () => clearInterval(interval);
  }, [snapshot]);

  // ───── Drain (state hop only) ─────
  useEffect(() => {
    if (reveal) return;
    if (queue.length === 0) return;
    const next = queue[0]!;
    setQueue((q) => q.slice(1));
    setReveal(next);
    setPulseKey((k) => k + 1);
  }, [reveal, queue]);

  // ───── Hold timer (clears reveal so drain picks up next) ─────
  useEffect(() => {
    if (!reveal) return;
    const t = setTimeout(() => setReveal(null), bannerHoldMs);
    return () => clearTimeout(t);
  }, [reveal, bannerHoldMs]);

  // Hooks must be called in the same order every render — keep the useMemo
  // ABOVE any conditional early return.
  const bonusSec = useMemo(() => {
    if (!snapshot?.endsAt || !snapshot?.startedAt || !snapshot?.initialDurationSec) return 0;
    const total =
      (new Date(snapshot.endsAt).getTime() - new Date(snapshot.startedAt).getTime()) / 1000;
    return Math.max(0, Math.floor(total - snapshot.initialDurationSec));
  }, [snapshot?.endsAt, snapshot?.startedAt, snapshot?.initialDurationSec]);

  const hasActiveMarathon = snapshot?.isActive === true;
  if (!hasActiveMarathon && !reveal && queue.length === 0) return null;

  const isPaused = snapshot?.pausedAt != null;
  const initialSec = snapshot?.initialDurationSec ?? 0;

  const topContributors = (snapshot?.topContributors ?? []).slice(0, maxLeaderboardEntries);
  const activity = (snapshot?.recent ?? []).slice(0, maxActivityItems);

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        padding: 12,
        fontFamily: "'Inter', system-ui, sans-serif",
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          width: '100%',
          height: '100%',
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 18,
          boxSizing: 'border-box',
          padding: 22,
          color: '#fff',
          background: `
            radial-gradient(circle at 18% 88%, rgba(${accentRgb}, 0.18), transparent 50%),
            radial-gradient(circle at 82% 12%, rgba(${accentRgb}, 0.12), transparent 50%),
            linear-gradient(180deg, rgba(15,12,30,0.94) 0%, rgba(8,6,18,0.97) 100%)
          `,
          boxShadow: `
            0 24px 60px -20px rgba(0,0,0,0.8),
            0 0 0 1px rgba(255,255,255,0.06),
            inset 0 0 0 1px rgba(${accentRgb}, ${reveal ? 0.35 : 0.12})
          `,
          transition: 'box-shadow 350ms ease',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        {/* Background drifting blobs — slow ambient motion */}
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: -100,
            pointerEvents: 'none',
            background: `radial-gradient(circle at 50% 50%, rgba(${accentRgb}, ${reveal ? 0.12 : 0.04}) 0%, transparent 40%)`,
            animation: 'mf-drift 22s ease-in-out infinite',
            transition: 'background 350ms ease',
          }}
        />

        {/* Header row: marathon name + reveal banner slot */}
        <div
          style={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            minHeight: 28,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: isPaused ? '#facc15' : '#22c55e',
                boxShadow: `0 0 8px ${isPaused ? 'rgba(250,204,21,0.7)' : 'rgba(34,197,94,0.7)'}`,
                flexShrink: 0,
                animation: isPaused ? 'none' : 'mf-pulse-dot 2s ease-in-out infinite',
              }}
            />
            <span
              style={{
                fontSize: 11,
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: 2,
                color: 'rgba(255,255,255,0.85)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {snapshot?.name ?? 'Marathon'}
              {isPaused && <span style={{ marginLeft: 8, opacity: 0.6 }}>· PAUSED</span>}
            </span>
          </div>

          {/* Banner reveal slot */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '6px 14px',
              borderRadius: 999,
              background: reveal ? 'rgba(255,255,255,0.06)' : 'transparent',
              border: `1px solid ${reveal ? accent + '88' : 'transparent'}`,
              opacity: reveal ? 1 : 0,
              transform: reveal ? 'translateX(0)' : 'translateX(20px)',
              transition:
                'opacity 280ms ease, transform 320ms cubic-bezier(0.16, 1, 0.3, 1), background 280ms ease, border 280ms ease',
              boxShadow: reveal ? `0 0 24px rgba(${accentRgb}, 0.35)` : 'none',
              pointerEvents: 'none',
            }}
          >
            {reveal && (
              <>
                <span
                  style={{
                    fontSize: 18,
                    fontWeight: 900,
                    color: accent,
                    textShadow: `0 0 12px rgba(${accentRgb}, 0.6)`,
                    letterSpacing: '-0.02em',
                  }}
                >
                  {formatAdded(reveal.secondsAdded)}
                </span>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 1.2,
                    color: 'rgba(255,255,255,0.85)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <span style={{ color: '#fff' }}>{reveal.contributorName}</span>
                  <span style={{ opacity: 0.45 }}> · </span>
                  <span>{EVENT_LABEL[reveal.eventType] ?? reveal.eventType.toUpperCase()}</span>
                </span>
              </>
            )}
          </div>
        </div>

        {/* Hero countdown */}
        <div
          style={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 4,
            padding: '4px 0',
          }}
        >
          <div
            key={pulseKey}
            style={{
              fontSize: 88,
              fontWeight: 900,
              lineHeight: 1,
              letterSpacing: '-0.04em',
              fontVariantNumeric: 'tabular-nums',
              backgroundImage: gradient,
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              color: 'transparent',
              WebkitTextFillColor: 'transparent',
              filter: `drop-shadow(0 0 24px rgba(${accentRgb}, 0.35))`,
              animation: 'mf-hero-pulse 700ms cubic-bezier(0.16, 1, 0.3, 1)',
            }}
          >
            {formatTime(remaining)}
          </div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 800,
              textTransform: 'uppercase',
              letterSpacing: 4,
              color: 'rgba(255,255,255,0.45)',
            }}
          >
            {isPaused ? 'Paused' : 'Remaining'}
          </div>
        </div>

        {/* Four-stat strip */}
        {showStats && (
          <div
            style={{
              position: 'relative',
              zIndex: 1,
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 1,
              borderRadius: 10,
              overflow: 'hidden',
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.06)',
            }}
          >
            <StatCell label="Initial" value={formatDuration(initialSec)} accent={accent} />
            <StatCell
              label="Bonus"
              value={`+${formatDuration(bonusSec)}`}
              accent={accent}
              highlight
            />
            <StatCell
              label="Supporters"
              value={String(snapshot?.uniqueContributorCount ?? 0)}
              accent={accent}
            />
            <StatCell
              label="Events"
              value={String(snapshot?.contributionCount ?? 0)}
              accent={accent}
            />
          </div>
        )}

        {/* Leaderboard + activity */}
        {(showLeaderboard || showActivity) && (
          <div
            style={{
              position: 'relative',
              zIndex: 1,
              flex: 1,
              minHeight: 0,
              display: 'grid',
              gridTemplateColumns: showLeaderboard && showActivity ? '2fr 3fr' : '1fr',
              gap: 12,
            }}
          >
            {showLeaderboard && (
              <Panel title="Top Supporters" accent={accent}>
                {topContributors.length === 0 ? (
                  <EmptyHint text="Waiting for first contribution…" />
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {topContributors.map((c, i) => (
                      <LeaderboardRow
                        key={`${c.contributorName ?? 'anon'}-${i}`}
                        rank={i + 1}
                        name={c.contributorName ?? 'Anonymous'}
                        seconds={c.totalSeconds}
                        accent={accent}
                      />
                    ))}
                  </div>
                )}
              </Panel>
            )}

            {showActivity && (
              <Panel title="Live Activity" accent={accent}>
                {activity.length === 0 ? (
                  <EmptyHint text="Live activity will appear here." />
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 4,
                      minHeight: 0,
                      overflow: 'hidden',
                    }}
                  >
                    {activity.map((r, i) => (
                      <ActivityRow
                        key={r.createdAt}
                        // Newest entry (i === 0) animates in to highlight the new arrival.
                        isNewest={i === 0}
                        eventType={r.eventType}
                        contributorName={r.contributorName ?? 'Someone'}
                        secondsAdded={r.secondsAdded}
                        accent={accent}
                      />
                    ))}
                  </div>
                )}
              </Panel>
            )}
          </div>
        )}

        {/* Animations */}
        <style>{`
          @keyframes mf-drift {
            0%   { transform: translate(0, 0) scale(1); }
            33%  { transform: translate(40px, -30px) scale(1.05); }
            66%  { transform: translate(-30px, 35px) scale(0.97); }
            100% { transform: translate(0, 0) scale(1); }
          }
          @keyframes mf-hero-pulse {
            0%   { transform: scale(1); filter: drop-shadow(0 0 24px rgba(${accentRgb}, 0.35)); }
            40%  { transform: scale(1.04); filter: drop-shadow(0 0 36px rgba(${accentRgb}, 0.7)); }
            100% { transform: scale(1); filter: drop-shadow(0 0 24px rgba(${accentRgb}, 0.35)); }
          }
          @keyframes mf-pulse-dot {
            0%, 100% { opacity: 1; transform: scale(1); }
            50%      { opacity: 0.5; transform: scale(0.85); }
          }
          @keyframes mf-row-in {
            from { opacity: 0; transform: translateY(-8px); background-color: rgba(${accentRgb}, 0.18); }
            to   { opacity: 1; transform: translateY(0); background-color: transparent; }
          }
        `}</style>
      </div>
    </div>
  );
}

function Panel({
  title,
  accent,
  children,
}: {
  title: string;
  accent: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: '12px 14px',
        borderRadius: 12,
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.06)',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <span
          style={{
            width: 4,
            height: 12,
            borderRadius: 2,
            background: accent,
            boxShadow: `0 0 8px ${accent}`,
          }}
        />
        <span
          style={{
            fontSize: 10,
            fontWeight: 800,
            textTransform: 'uppercase',
            letterSpacing: 2,
            color: 'rgba(255,255,255,0.7)',
          }}
        >
          {title}
        </span>
      </div>
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>{children}</div>
    </div>
  );
}

function StatCell({
  label,
  value,
  accent,
  highlight,
}: {
  label: string;
  value: string;
  accent: string;
  highlight?: boolean;
}) {
  return (
    <div
      style={{
        padding: '10px 12px',
        background: 'rgba(0,0,0,0.25)',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <div
        style={{
          fontSize: 9,
          fontWeight: 800,
          textTransform: 'uppercase',
          letterSpacing: 1.6,
          color: 'rgba(255,255,255,0.5)',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 18,
          fontWeight: 900,
          color: highlight ? accent : '#fff',
          letterSpacing: '-0.01em',
          lineHeight: 1.1,
          textShadow: highlight ? `0 0 12px ${accent}55` : 'none',
        }}
      >
        {value}
      </div>
    </div>
  );
}

function LeaderboardRow({
  rank,
  name,
  seconds,
  accent,
}: {
  rank: number;
  name: string;
  seconds: number;
  accent: string;
}) {
  const isPodium = rank <= 3;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '6px 4px',
        borderRadius: 6,
      }}
    >
      <span
        style={{
          width: 22,
          height: 22,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          fontSize: 11,
          fontWeight: 900,
          background: isPodium ? accent : 'rgba(255,255,255,0.08)',
          color: isPodium ? '#0b0918' : 'rgba(255,255,255,0.7)',
          boxShadow: isPodium ? `0 0 8px ${accent}aa` : 'none',
          flexShrink: 0,
        }}
      >
        {rank}
      </span>
      <span
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: 13,
          fontWeight: 700,
          color: '#fff',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {name}
      </span>
      <span
        style={{
          fontSize: 12,
          fontWeight: 800,
          color: accent,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        +{formatDuration(seconds) || '0m'}
      </span>
    </div>
  );
}

function ActivityRow({
  isNewest,
  eventType,
  contributorName,
  secondsAdded,
  accent,
}: {
  isNewest: boolean;
  eventType: string;
  contributorName: string;
  secondsAdded: number;
  accent: string;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '5px 6px',
        borderRadius: 6,
        fontSize: 12,
        animation: isNewest ? 'mf-row-in 600ms ease-out' : undefined,
      }}
    >
      <span
        style={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          fontSize: 11,
          fontWeight: 900,
          background: 'rgba(255,255,255,0.06)',
          color: accent,
          flexShrink: 0,
        }}
      >
        {EVENT_GLYPH[eventType] ?? '•'}
      </span>
      <span
        style={{
          fontWeight: 700,
          color: '#fff',
          flex: '0 1 auto',
          minWidth: 0,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {contributorName}
      </span>
      <span
        style={{
          fontSize: 9,
          fontWeight: 800,
          textTransform: 'uppercase',
          letterSpacing: 1,
          color: 'rgba(255,255,255,0.45)',
          flexShrink: 0,
        }}
      >
        {EVENT_LABEL[eventType] ?? eventType.toUpperCase()}
      </span>
      <span style={{ flex: 1 }} />
      <span
        style={{
          fontWeight: 800,
          color: accent,
          fontVariantNumeric: 'tabular-nums',
          flexShrink: 0,
        }}
      >
        +{formatDuration(secondsAdded)}
      </span>
    </div>
  );
}

function EmptyHint({ text }: { text: string }) {
  return (
    <div
      style={{
        fontSize: 11,
        color: 'rgba(255,255,255,0.35)',
        fontStyle: 'italic',
        padding: '8px 4px',
      }}
    >
      {text}
    </div>
  );
}
